/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useTypedSelector} from 'helpers/reducers'
import Loader from 'components/Loader'
import {consoleLog, currencyNumberFormat} from 'utils/utility'
import BookingPackageItemsDurations from 'pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations'
import {DEFAULT_HALL_ID} from 'helpers/config'
import {PackageCategory} from 'types/enums/PackageCategory'
import BookingAction from 'store/booking/BookingAction'
import {PackagesItemByPerson, PackagesItemsPerson} from 'types/entities/PackagesItems'
import {hasKartingPackageItems} from 'utils/packageUtils'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {useAppDispatch} from 'helpers/store'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {MinusIcon, PlusIcon} from '@heroicons/react/24/solid'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {PersonType} from 'types/enums/PersonType'
import clsx from 'clsx'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import {usePackageCategory} from 'utils/hooks/usePackageCategory'

interface BookingPackagesEventProps {
    packageCategory: PackageCategory
    onContinue: () => void
    onBack?: () => void
    isBooking: boolean
}

const PackagesSetupCard = (props: {
    children: React.ReactNode | React.ReactNode[]
    color: 'blue' | 'amber' | 'green'
}) => {
    const colors = (color: 'blue' | 'amber' | 'green') => {
        switch (color) {
            case 'blue':
                return 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'
            case 'amber':
                return 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800'
            case 'green':
                return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
        }
    }
    return (
        <div
            className={clsx(
                'PackagesSetupCard grid grid-cols-2 items-center justify-center justify-items-center gap-x-4 rounded-lg p-3 border',
                colors(props.color)
            )}
        >
            {props.children}
        </div>
    )
}

type PersonKartingCounts = {
    withKarting: number
    withoutKarting: number
}

type KartingCountsByPersonType = {
    adult: PersonKartingCounts
    child: PersonKartingCounts
}

type PackageVariantDefaults = {
    withKarting: PackagesItemsPerson | null
    withoutKarting: PackagesItemsPerson | null
}

type DefaultPackagesByPersonType = {
    adult: PackageVariantDefaults
    child: PackageVariantDefaults
}

const PACKAGE_SELECTION_INDEX = {
    adultWithKarting: 0,
    adultWithoutKarting: 1,
    childWithKarting: 2,
    childWithoutKarting: 3,
} as const

const clampCount = (value: number, max: number) => Math.max(0, Math.min(max, Number.isFinite(value) ? value : 0))

const createDefaultKartingCounts = (count: number, creditBased: boolean, kartingAvailable: boolean): PersonKartingCounts => {
    if (!kartingAvailable || creditBased) {
        return {
            withKarting: 0,
            withoutKarting: count,
        }
    }
    return {
        withKarting: count,
        withoutKarting: 0,
    }
}

const normalizeKartingCounts = (
    current: PersonKartingCounts,
    nextCount: number,
    previousCount: number,
    creditBased: boolean,
    kartingAvailable: boolean
): PersonKartingCounts => {
    const normalizedCount = Math.max(0, nextCount)

    if (!kartingAvailable) {
        return createDefaultKartingCounts(normalizedCount, creditBased, kartingAvailable)
    }

    if (previousCount === 0 && current.withKarting === 0 && current.withoutKarting === 0) {
        return createDefaultKartingCounts(normalizedCount, creditBased, kartingAvailable)
    }

    if (normalizedCount >= previousCount) {
        const difference = normalizedCount - previousCount
        return creditBased
            ? {
                  withKarting: clampCount(current.withKarting, normalizedCount),
                  withoutKarting: normalizedCount - clampCount(current.withKarting, normalizedCount),
              }
            : {
                  withKarting: clampCount(current.withKarting + difference, normalizedCount),
                  withoutKarting: normalizedCount - clampCount(current.withKarting + difference, normalizedCount),
              }
    }

    const withKarting = clampCount(current.withKarting, normalizedCount)
    return {
        withKarting,
        withoutKarting: normalizedCount - withKarting,
    }
}

const emptyDefaultPackages: DefaultPackagesByPersonType = {
    adult: {
        withKarting: null,
        withoutKarting: null,
    },
    child: {
        withKarting: null,
        withoutKarting: null,
    },
}

const SellerPackagesEvent = (props: BookingPackagesEventProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const availablePackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.availablePackageItems)
    const filter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)
    const selectedTimeslot = useTypedSelector((state) => state.bookingState.bookingProcess.selectedTimeslot)
    const seller_id = useTypedSelector((state) => state.sessionState.session?.uid) || 1
    const { selectedCategoryEntity } = usePackageCategory()
    const kartingAvailable = !selectedCategoryEntity.extra?.releaseDate
    const hasKartingItems = useMemo(
        () => hasKartingPackageItems(availablePackageItems?.packageItems),
        [availablePackageItems]
    )
    const kartingSplitEnabled = kartingAvailable && hasKartingItems

    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240)
    const [karting, setKarting] = useState<KartingCountsByPersonType>(() => ({
        adult: createDefaultKartingCounts(filter.adults || 0, selectedCategoryEntity.creditBased, kartingSplitEnabled),
        child: createDefaultKartingCounts(filter.children || 0, selectedCategoryEntity.creditBased, kartingSplitEnabled),
    }))

    const prevAdultCountRef = useRef<number>(filter.adults || 0)
    const prevChildCountRef = useRef<number>(filter.children || 0)
    const prevPackageCategoryRef = useRef<PackageCategory>(props.packageCategory)
    const isInitialPackageItemsFetchRef = useRef(true)

    const fetchPackageItemsRequest = useCallback(() => {
        return dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                {
                    adults: filter.adults,
                    children: filter.children,
                    date: filter.date,
                    package_category_name: props.packageCategory,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
                },
                seller_id
            )
        ).then((r) => {
            consoleLog('fetchPackagesByCategoryAndTimeSlot: res', r)
        })
    }, [
        dispatch,
        filter.activity_ids,
        filter.adults,
        filter.children,
        filter.date,
        props.packageCategory,
        selectedTimeslot,
        seller_id,
    ])

    const debouncedFetchPackageItems = useDebouncedCallback(fetchPackageItemsRequest, 500)

    const defaultPackages = useMemo<DefaultPackagesByPersonType>(() => {
        if (!availablePackageItems || !chosenDuration) {
            return emptyDefaultPackages
        }

        const findPackageForCombination = (
            personType: PackageItemPersonType.Adult | PackageItemPersonType.Child,
            withKarting: boolean
        ): PackagesItemsPerson | null => {
            const packageItems = availablePackageItems.packageItems[personType]
            return (
                packageItems.find(
                    (item) =>
                        item.duration === chosenDuration &&
                        item.karting === withKarting &&
                        item.person_type === personType
                ) || null
            )
        }

        return {
            adult: {
                withKarting: findPackageForCombination(PackageItemPersonType.Adult, true),
                withoutKarting: findPackageForCombination(PackageItemPersonType.Adult, false),
            },
            child: {
                withKarting: findPackageForCombination(PackageItemPersonType.Child, true),
                withoutKarting: findPackageForCombination(PackageItemPersonType.Child, false),
            },
        }
    }, [availablePackageItems, chosenDuration])

    const selectedPackageItems = useMemo<PackagesItemByPerson[]>(() => {
        const rows: PackagesItemByPerson[] = []

        const addRow = (
            quantity: number,
            packageItem: PackagesItemsPerson | null,
            personType: PackageItemPersonType.Adult | PackageItemPersonType.Child,
            personIndex: number
        ) => {
            if (quantity <= 0 || !packageItem || packageItem.person_type !== personType) {
                return
            }

            rows.push({
                personIndex,
                id: packageItem.id,
                person_type: packageItem.person_type,
                quantity,
            })
        }

        addRow(
            karting.adult.withKarting,
            defaultPackages.adult.withKarting,
            PackageItemPersonType.Adult,
            PACKAGE_SELECTION_INDEX.adultWithKarting
        )
        addRow(
            karting.adult.withoutKarting,
            defaultPackages.adult.withoutKarting,
            PackageItemPersonType.Adult,
            PACKAGE_SELECTION_INDEX.adultWithoutKarting
        )
        addRow(
            karting.child.withKarting,
            defaultPackages.child.withKarting,
            PackageItemPersonType.Child,
            PACKAGE_SELECTION_INDEX.childWithKarting
        )
        addRow(
            karting.child.withoutKarting,
            defaultPackages.child.withoutKarting,
            PackageItemPersonType.Child,
            PACKAGE_SELECTION_INDEX.childWithoutKarting
        )

        return rows
    }, [defaultPackages, karting])

    const totalPrice = useMemo(() => {
        const linePrice = (quantity: number, packageItem: PackagesItemsPerson | null) =>
            quantity > 0 && packageItem ? quantity * packageItem.price : 0

        return (
            linePrice(karting.adult.withKarting, defaultPackages.adult.withKarting) +
            linePrice(karting.adult.withoutKarting, defaultPackages.adult.withoutKarting) +
            linePrice(karting.child.withKarting, defaultPackages.child.withKarting) +
            linePrice(karting.child.withoutKarting, defaultPackages.child.withoutKarting)
        )
    }, [defaultPackages, karting])

    const allFilled = useMemo(() => {
        const adultsSum = karting.adult.withKarting + karting.adult.withoutKarting
        const childrenSum = karting.child.withKarting + karting.child.withoutKarting

        const hasRequiredPackages =
            (karting.adult.withKarting === 0 || defaultPackages.adult.withKarting !== null) &&
            (karting.adult.withoutKarting === 0 || defaultPackages.adult.withoutKarting !== null) &&
            (karting.child.withKarting === 0 || defaultPackages.child.withKarting !== null) &&
            (karting.child.withoutKarting === 0 || defaultPackages.child.withoutKarting !== null)

        const adultSelectedQuantity = selectedPackageItems
            .filter((item) => item.person_type === PackageItemPersonType.Adult)
            .reduce((sum, item) => sum + item.quantity, 0)
        const childSelectedQuantity = selectedPackageItems
            .filter((item) => item.person_type === PackageItemPersonType.Child)
            .reduce((sum, item) => sum + item.quantity, 0)

        return (
            !!chosenDuration &&
            adultsSum === filter.adults &&
            childrenSum === filter.children &&
            hasRequiredPackages &&
            adultSelectedQuantity === filter.adults &&
            childSelectedQuantity === filter.children
        )
    }, [chosenDuration, defaultPackages, filter.adults, filter.children, karting, selectedPackageItems])

    const isDurationAvailable = useCallback(
        (duration: number) => {
            const adultItems = availablePackageItems?.packageItems?.adult || []
            return adultItems.some((item) => item.duration === duration)
        },
        [availablePackageItems]
    )

    const suggestLeastPossibleDuration = useCallback(() => {
        // Prefer common event durations first; include longer ones offered by mega_fun packages.
        const durations = [180, 240, 270, 360, 480, 120, 60]

        for (const duration of durations) {
            if (isDurationAvailable(duration)) {
                setChosenDuration(duration)
                return
            }
        }
    }, [isDurationAvailable])

    const changeFilterForPersonType = (personType: PersonType, value: number) => {
        if (value < 0) {
            value = 0
        }
        switch (personType) {
            case PersonType.Adult:
                dispatch(
                    BookingAction.setFilter({
                        ...filter,
                        adults: value,
                    })
                )
                break
            case PersonType.Child:
                dispatch(
                    BookingAction.setFilter({
                        ...filter,
                        children: value,
                    })
                )
                break
        }
    }

    useEffect(() => {
        if (!availablePackageItems?.packageItems) {
            return
        }

        // Keep the seller's selection unless it is missing/invalid for the loaded packages.
        if (typeof chosenDuration === 'number' && isDurationAvailable(chosenDuration)) {
            return
        }

        suggestLeastPossibleDuration()
    }, [availablePackageItems, chosenDuration, isDurationAvailable, suggestLeastPossibleDuration])

    useEffect(() => {
        const categoryChanged = prevPackageCategoryRef.current !== props.packageCategory
        prevPackageCategoryRef.current = props.packageCategory

        if (isInitialPackageItemsFetchRef.current || categoryChanged) {
            isInitialPackageItemsFetchRef.current = false
            debouncedFetchPackageItems.cancel()
            dispatch(BookingAction.clearAvailablePackagesItems())
            void fetchPackageItemsRequest()
            return
        }

        debouncedFetchPackageItems()
    }, [
        debouncedFetchPackageItems,
        dispatch,
        fetchPackageItemsRequest,
        filter.adults,
        filter.children,
        props.packageCategory,
    ])

    useEffect(() => {
        return () => {
            debouncedFetchPackageItems.cancel()
        }
    }, [debouncedFetchPackageItems])

    useEffect(() => {
        dispatch(BookingAction.setSelectedPackageItems(selectedPackageItems))
    }, [dispatch, selectedPackageItems])

    useEffect(() => {
        setKarting((prev) => {
            const previousAdultCount = prevAdultCountRef.current
            const previousChildCount = prevChildCountRef.current
            prevAdultCountRef.current = filter.adults
            prevChildCountRef.current = filter.children

            return {
                adult: normalizeKartingCounts(
                    prev.adult,
                    filter.adults,
                    previousAdultCount,
                    selectedCategoryEntity.creditBased,
                    kartingSplitEnabled
                ),
                child: normalizeKartingCounts(
                    prev.child,
                    filter.children,
                    previousChildCount,
                    selectedCategoryEntity.creditBased,
                    kartingSplitEnabled
                ),
            }
        })
    }, [filter.adults, filter.children, selectedCategoryEntity.creditBased, kartingSplitEnabled])

    if (!availablePackageItems) {
        return <Loader />
    }

    const formatPrice = (price: number) => currencyNumberFormat(price)

    const setKartingCount = (
        personType: keyof KartingCountsByPersonType,
        field: keyof PersonKartingCounts,
        value: number
    ) => {
        const maxCount = personType === 'adult' ? filter.adults : filter.children
        const nextValue = clampCount(value, maxCount)

        setKarting((prev) => ({
            ...prev,
            [personType]:
                field === 'withKarting'
                    ? {
                          withKarting: nextValue,
                          withoutKarting: maxCount - nextValue,
                      }
                    : {
                          withKarting: maxCount - nextValue,
                          withoutKarting: nextValue,
                      },
        }))
    }

    const transferKartingCount = (
        personType: keyof KartingCountsByPersonType,
        from: keyof PersonKartingCounts,
        to: keyof PersonKartingCounts
    ) => {
        if (!kartingSplitEnabled) {
            return
        }

        setKarting((prev) => {
            if (prev[personType][from] <= 0) {
                return prev
            }

            return {
                ...prev,
                [personType]: {
                    ...prev[personType],
                    [from]: prev[personType][from] - 1,
                    [to]: prev[personType][to] + 1,
                },
            }
        })
    }

    const renderKartingAllocationControls = (
        personType: keyof KartingCountsByPersonType,
        label: string,
        maxCount: number
    ) => {
        const counts = karting[personType]
        const controls = [
            {
                label: t('without Karting'),
                field: 'withoutKarting' as const,
                decrementTo: 'withKarting' as const,
                incrementFrom: 'withKarting' as const,
            },
            {
                label: t('with Karting'),
                field: 'withKarting' as const,
                decrementTo: 'withoutKarting' as const,
                incrementFrom: 'withoutKarting' as const,
                hidden: !kartingSplitEnabled,
            },
        ]

        return (
            <div className={'flex flex-col gap-2 w-full'}>
                <div className={'text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide'}>
                    {label}
                </div>
                {controls
                    .filter((control) => !control.hidden)
                    .map((control) => (
                        <div key={`${personType}-${control.field}`} className={'flex flex-col gap-1'}>
                            <label
                                className={
                                    'text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide whitespace-nowrap'
                                }
                            >
                                {control.label}
                            </label>
                            <div className={'grid grid-cols-[48px_60px_48px] items-center gap-2 w-full'}>
                                <UiButton
                                    style={UiButtonStyle.Amber}
                                    size={UiButtonSize.Normal}
                                    onClick={() => transferKartingCount(personType, control.field, control.decrementTo)}
                                >
                                    <MinusIcon className={'size-4'} />
                                </UiButton>
                                <input
                                    className={
                                        'flex-1 text-center h-9 text-sm font-bold border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-amber-900 text-gray-900 dark:text-amber-100'
                                    }
                                    inputMode={'numeric'}
                                    type={'number'}
                                    value={counts[control.field]}
                                    onChange={(e) => setKartingCount(personType, control.field, +e.target.value)}
                                    max={maxCount}
                                    min={0}
                                    disabled={!kartingSplitEnabled && control.field === 'withoutKarting'}
                                />
                                <UiButton
                                    style={UiButtonStyle.Amber}
                                    size={UiButtonSize.Normal}
                                    onClick={() =>
                                        transferKartingCount(personType, control.incrementFrom, control.field)
                                    }
                                >
                                    <PlusIcon className={'size-4'} />
                                </UiButton>
                            </div>
                        </div>
                    ))}
            </div>
        )
    }

    const showChildControls = availablePackageItems.packageItems.child.length > 0 || filter.children > 0
    const showChildPriceRows = showChildControls

    return (
        <div className={'SellerPackagesEvent SellerBookingPackagesEventBasic w-full space-y-4'}>
            {/* Controls Row */}
            <div
                className={
                    'bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md overflow-hidden'
                }
            >
                <div className={'p-4 grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4'}>
                    {/* Participants Section */}

                    <PackagesSetupCard color={'blue'}>
                        {/* Adult Counter */}
                        <div className={'flex flex-col gap-1'}>
                            <label
                                className={
                                    'text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide'
                                }
                            >
                                {t('Adult')}
                            </label>
                            <div className={'grid grid-cols-[48px_60px_48px] items-center gap-2 w-full'}>
                                <UiButton
                                    style={UiButtonStyle.Blue}
                                    size={UiButtonSize.Normal}
                                    onClick={() => changeFilterForPersonType(PersonType.Adult, filter.adults - 1)}
                                >
                                    <MinusIcon className={'size-4'} />
                                </UiButton>
                                <input
                                    className={
                                        'text-center h-9 text-sm font-bold border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-blue-900 text-gray-900 dark:text-blue-100'
                                    }
                                    inputMode={'numeric'}
                                    type={'number'}
                                    onChange={(e) => {
                                        changeFilterForPersonType(PersonType.Adult, +e.target.value)
                                    }}
                                    onBlur={(e) => {
                                        changeFilterForPersonType(PersonType.Adult, +e.target.value)
                                    }}
                                    value={filter.adults}
                                />
                                <UiButton
                                    style={UiButtonStyle.Blue}
                                    size={UiButtonSize.Normal}
                                    onClick={() => changeFilterForPersonType(PersonType.Adult, filter.adults + 1)}
                                >
                                    <PlusIcon className={'size-4'} />
                                </UiButton>
                            </div>
                        </div>

                        {/* Child Counter */}
                        {availablePackageItems &&
                            availablePackageItems.packageItems &&
                            availablePackageItems.packageItems.child.length > 0 && (
                                <div className={'flex flex-col gap-1'}>
                                    <label
                                        className={
                                            'text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide'
                                        }
                                    >
                                        {t('Child')}
                                    </label>
                                    <div className={'grid grid-cols-[48px_60px_48px] items-center gap-2 w-full'}>
                                        <UiButton
                                            style={UiButtonStyle.Blue}
                                            size={UiButtonSize.Normal}
                                            onClick={() =>
                                                changeFilterForPersonType(PersonType.Child, filter.children - 1)
                                            }
                                        >
                                            <MinusIcon className={'size-4'} />
                                        </UiButton>
                                        <input
                                            className={
                                                'shrink grow-0 text-center h-9 text-sm font-bold border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-blue-900 text-gray-900 dark:text-blue-100'
                                            }
                                            inputMode={'numeric'}
                                            type={'number'}
                                            onChange={(e) => {
                                                changeFilterForPersonType(PersonType.Child, +e.target.value)
                                            }}
                                            onBlur={(e) => {
                                                changeFilterForPersonType(PersonType.Child, +e.target.value)
                                            }}
                                            value={filter.children}
                                        />
                                        <UiButton
                                            style={UiButtonStyle.Blue}
                                            size={UiButtonSize.Normal}
                                            onClick={() =>
                                                changeFilterForPersonType(PersonType.Child, filter.children + 1)
                                            }
                                        >
                                            <PlusIcon className={'size-4'} />
                                        </UiButton>
                                    </div>
                                </div>
                            )}
                    </PackagesSetupCard>

                    {hasKartingItems && (
                        <PackagesSetupCard color={'amber'}>
                            {renderKartingAllocationControls('adult', t('Adults'), filter.adults)}
                            {showChildControls &&
                                renderKartingAllocationControls('child', t('Children'), filter.children)}
                        </PackagesSetupCard>
                    )}

                    {/* Duration Section */}
                    <PackagesSetupCard color={'green'}>
                        <div className={'flex flex-col col-span-2'}>
                            <label
                                className={
                                    'text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide'
                                }
                            >
                                {t('Duration')}
                            </label>
                            <BookingPackageItemsDurations
                                disabled1Hour={true}
                                disabled2Hours={false}
                                chosenDuration={chosenDuration}
                                className={'justify-start sm:justify-center'}
                                enabled={true}
                                personType={PackageItemPersonType.Adult}
                                setChosenDuration={setChosenDuration}
                                isSeller={true}
                            />
                        </div>
                    </PackagesSetupCard>
                </div>
            </div>
            {/* Summary Section */}
            <div
                className={
                    'bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md p-4'
                }
            >
                <div className={'flex items-center justify-between'}>
                    <div className={'grid grid-cols-2 gap-x-6 gap-y-3'}>
                        <div>
                            <p
                                className={
                                    'text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'
                                }
                            >
                                {t('Adult')} {t('without Karting')}
                            </p>
                            <p className={'text-lg font-bold text-gray-900 dark:text-gray-100'}>
                                {formatPrice(defaultPackages.adult.withoutKarting?.price || 0)}
                            </p>
                        </div>
                        <div>
                            <p
                                className={
                                    'text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'
                                }
                            >
                                {t('Adult')} {t('with Karting')}
                            </p>
                            <p className={'text-lg font-bold text-gray-900 dark:text-gray-100'}>
                                {formatPrice(defaultPackages.adult.withKarting?.price || 0)}
                            </p>
                        </div>
                        {showChildPriceRows && (
                            <>
                                <div>
                                    <p
                                        className={
                                            'text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'
                                        }
                                    >
                                        {t('Child')} {t('without Karting')}
                                    </p>
                                    <p className={'text-lg font-bold text-gray-900 dark:text-gray-100'}>
                                        {formatPrice(defaultPackages.child.withoutKarting?.price || 0)}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        className={
                                            'text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'
                                        }
                                    >
                                        {t('Child')} {t('with Karting')}
                                    </p>
                                    <p className={'text-lg font-bold text-gray-900 dark:text-gray-100'}>
                                        {formatPrice(defaultPackages.child.withKarting?.price || 0)}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={'text-right'}>
                        <p className={'text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'}>
                            {t('Total price')}
                        </p>
                        <p className={'text-2xl font-bold text-amber-600 dark:text-amber-500'}>
                            {formatPrice(totalPrice)}
                        </p>
                    </div>
                </div>
            </div>

            <UiFormActionsBar
                backText={props.onBack ? t('Back') : undefined}
                onBack={props.onBack}
                primaryText={t('Continue')}
                floatRight={true}
                disabled={props.isBooking || !allFilled}
                onPrimary={props.onContinue}
            />
        </div>
    )
}

export default SellerPackagesEvent
